import { type RefObject, useLayoutEffect, useRef } from 'react'
import {
  afterAnimation,
  readsRtl,
  sigAnimate,
  sigEase,
  sigMs,
  signatureMoves,
} from '../../lib/signature/motion'

/** A save adds one row, two at most; more than that is a different list. */
const MOST_ADDED = 2
const SEPARATOR = '\u0000'

/**
 * The rows that appeared while the rest of the list stayed: what a save
 * added. A page change, a filter or the first load replaces the rows, and a
 * sort only reorders them, so each of those names nothing.
 */
export function newRowIds(before: readonly string[] | null, after: readonly string[]): string[] {
  if (!before || before.length === 0) return []
  const kept = new Set(before)
  const added = after.filter((id) => !kept.has(id))
  const stayed = after.length - added.length
  if (added.length === 0 || added.length > MOST_ADDED) return []
  // A new row at the top of a full page pushes as many rows off its end.
  if (stayed === 0 || stayed < before.length - added.length) return []
  return added
}

/**
 * The new row soaks in grey ink from the reading start, then clears
 * (DESIGN.md "The signature layer", "The new row"). The ink is a layer over the
 * row that exists only while it moves; it blends so the row's text stays
 * readable through it.
 */
function soakRow(row: HTMLElement) {
  const container = row.closest<HTMLElement>('[data-slot="table-container"]')
  if (!container) return
  const box = row.getBoundingClientRect()
  const frame = container.getBoundingClientRect()
  const ink = document.createElement('div')
  ink.dataset.signature = 'soak'
  ink.setAttribute('aria-hidden', 'true')
  Object.assign(ink.style, {
    position: 'absolute',
    left: `${box.left - frame.left + container.scrollLeft}px`,
    top: `${box.top - frame.top + container.scrollTop}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    background: 'var(--brand-subtle)',
    mixBlendMode: document.documentElement.classList.contains('dark') ? 'lighten' : 'darken',
    pointerEvents: 'none',
  })
  container.append(ink)

  const ease = sigEase('--sig-ease-write')
  const hidden = readsRtl(row) ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)'
  const soak = sigAnimate(ink, [{ clipPath: hidden }, { clipPath: 'inset(0 0 0 0)' }], {
    duration: sigMs('--sig-dur-soak', 420),
    easing: ease,
    fill: 'forwards',
  })
  afterAnimation(soak, () => {
    const clear = sigAnimate(ink, [{ opacity: 1 }, { opacity: 0 }], {
      duration: sigMs('--sig-dur-clear', 180),
      easing: ease,
      fill: 'forwards',
    })
    afterAnimation(clear, () => ink.remove())
  })
}

/** Soak the rows a save added to the table body behind `bodyRef`. `rowIds` is null while loading. */
export function useNewRowSoak(
  bodyRef: RefObject<HTMLElement | null>,
  rowIds: readonly string[] | null,
) {
  const previous = useRef<readonly string[] | null>(null)
  // One string, so the effect runs when the ids change, not when a new array
  // with the same ids arrives on a re-render.
  const key = rowIds === null ? null : rowIds.join(SEPARATOR)

  useLayoutEffect(() => {
    if (key === null) return
    const ids = key === '' ? [] : key.split(SEPARATOR)
    const added = newRowIds(previous.current, ids)
    previous.current = ids
    const body = bodyRef.current
    if (!body || added.length === 0 || !signatureMoves()) return
    const rows = new Map(
      [...body.querySelectorAll<HTMLElement>(':scope > [data-row-id]')].map((row) => [
        row.dataset.rowId,
        row,
      ]),
    )
    for (const id of added) {
      const row = rows.get(id)
      if (row) soakRow(row)
    }
  }, [key, bodyRef])
}
